import logger from '../utils/logger';
// smartreader-frontend/src/services/tag-events-socket.js
import { io } from 'socket.io-client';

let socket = null;

/**
 * Connect to the TagEvents WebSocket namespace
 * @param {Function} onTagEvent - Callback for tag events
 * @param {Function} onConnectionChange - Callback for connection state changes
 * @returns {Socket} The Socket.IO socket instance
 */
export const connectTagEventsSocket = (onTagEvent, onConnectionChange) => {
  if (socket?.connected) {
    return socket;
  }

  socket = io('/tag-events', {
    path: '/socket.io',
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  });

  socket.on('connect', () => {
    logger.log('[TagEvents] Socket connected:', socket.id);
    onConnectionChange?.({ connected: true, socketId: socket.id });
  });

  socket.on('disconnect', (reason) => {
    logger.log('[TagEvents] Socket disconnected:', reason);
    onConnectionChange?.({ connected: false, reason });
  });

  socket.on('connect_error', (error) => {
    logger.error('[TagEvents] Socket connection error:', error.message);
    onConnectionChange?.({ connected: false, error: error.message });
  });

  socket.on('tagEvent', (data) => {
    onTagEvent?.(data);
  });

  return socket;
};

/**
 * Subscribe to specific devices
 * @param {string[]} deviceSerials - Array of device serial numbers
 * @returns {Promise} Resolves with subscription result
 */
export const subscribeToDevices = (deviceSerials) => {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Socket not connected'));
      return;
    }

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        logger.warn('[TagEvents] Subscribe ack timeout — server did not respond');
        resolve({ success: false, reason: 'timeout', subscribed: [] });
      }
    }, 3000);

    socket.emit('subscribe', { devices: deviceSerials }, (response) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (response?.success) {
        logger.log('[TagEvents] Subscribed to devices:', response.subscribed);
        resolve(response);
      } else {
        reject(new Error('Subscription failed'));
      }
    });
  });
};

/**
 * Subscribe to all devices
 * @returns {Promise} Resolves with subscription result
 */
export const subscribeToAllDevices = () => {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Socket not connected'));
      return;
    }

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        logger.warn('[TagEvents] Subscribe-all ack timeout — server did not respond');
        resolve({ success: false, reason: 'timeout', subscribed: [] });
      }
    }, 3000);

    socket.emit('subscribe', { all: true }, (response) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (response?.success) {
        logger.log('[TagEvents] Subscribed to all devices');
        resolve(response);
      } else {
        reject(new Error('Subscription failed'));
      }
    });
  });
};

/**
 * Unsubscribe from specific devices
 * @param {string[]} deviceSerials - Array of device serial numbers
 */
export const unsubscribeFromDevices = (deviceSerials) => {
  if (socket?.connected) {
    socket.emit('unsubscribe', { devices: deviceSerials });
  }
};

/**
 * Unsubscribe from all devices
 */
export const unsubscribeFromAll = () => {
  if (socket?.connected) {
    socket.emit('unsubscribe', { all: true });
  }
};

/**
 * Disconnect the TagEvents socket
 */
export const disconnectTagEventsSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    logger.log('[TagEvents] Socket disconnected');
  }
};

/**
 * Get the current socket instance
 * @returns {Socket|null}
 */
export const getTagEventsSocket = () => socket;

/**
 * Check if socket is connected
 * @returns {boolean}
 */
export const isConnected = () => socket?.connected || false;

const tagEventsSocket = {
  connectTagEventsSocket,
  disconnectTagEventsSocket,
  subscribeToDevices,
  subscribeToAllDevices,
  unsubscribeFromDevices,
  unsubscribeFromAll,
  getTagEventsSocket,
  isConnected,
};

export default tagEventsSocket;
